import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource, ImageOptions, Photo } from '@capacitor/camera';
import { MediaUploadDetails } from 'src/app/models/media.model';
import { environment } from 'src/environments/environment';

const toleranceKb = 300;
const WEB_IMAGE_SIZE = (Number(environment.webAppFileSize) * 1024 * 1024) + (toleranceKb * 1024);
//gives a small tolerance of 300 KB above exact 10 MB
const WEB_PDF_SIZE = (Number(environment.webAppPdfSize) * 1024 * 1024) + (toleranceKb * 1024);

@Injectable({
  providedIn: 'root',
})
export class PhotoUploadService {
  private readonly defaultOptions: ImageOptions = {
    quality: 50,
    correctOrientation: true,
    allowEditing: false,
    width: 2048,
    height: 2048,
    saveToGallery: false,
    resultType: CameraResultType.Base64
  };

  ///function to capture image using camera or gallery
   async capture(source: CameraSource, prefix: string, desc: string): Promise<MediaUploadDetails | null> {
    try {
      const photo = await Camera.getPhoto({
        ...this.defaultOptions,
        source
      });
      return await this.photoWithDefaultName(photo, prefix, desc);
    } catch (err) {

      // User cancelled
      console.log(err);
      return null;
    }
  }

  ///generate file name and return photo object
  private photoWithDefaultName(photo: Photo, prefix: string, desc: string): MediaUploadDetails {

    const fileName = this.generateFileName(prefix);

    return {
      data:{
        base64Data: photo.base64String ?? ''
      },
      name: fileName,
      mimeType: 'image/jpeg',
      description: desc
    };
  }

  ///generate default file name
  ///prefix of the operation (ex: defect_rect, defect, photo (default))
  ///ext: extension of file (for mobile it will be default .jpg, for web it can be .jpeg/.jpg/.png)
  generateFileName(prefix: string = 'photo', ext: string = 'jpg'): string {

    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');

    return `${prefix}-${yyyy}${mm}${dd}${hh}${min}${ss}.${ext}`;
  }

  ///fetch max allowed size for image uploaded from web
  getMaxAllowedImageSize() {
    return {
      maxAllowedSize: WEB_IMAGE_SIZE,
      sizeLabel: environment.webAppFileSize
    };
  }

  ///fetch max allowed size for pdf uploaded from web
  getMaxAllowedPdfSize() {
    return {
      maxAllowedSize: WEB_PDF_SIZE,
      sizeLabel: environment.webAppPdfSize
    };
  }

  ///function to compress image file to reduce file size for payload optimization
  async compressImage(file: File, prefix: string = 'photo'): Promise<File> {
    const maxSize = 2048;
    const quality = 0.5;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = () => {
          let { width, height } = img;

          if (width > height && width > maxSize) {
            height = Math.round(height * (maxSize / width));
            width = maxSize;
          } else if (height > maxSize) {
            width = Math.round(width * (maxSize / height));
            height = maxSize;
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          const fileName = this.generateFileName(prefix, file.type?.split('/')[1] ?? 'jpg');
          canvas.toBlob(blob => {
            if (!blob) return reject(new Error('Compression failed'));

            resolve(new File([blob], fileName, {
              type: file.type ?? 'image/jpeg',
              lastModified: Date.now()
            }));
          }, (file.type ?? 'image/jpeg'), quality);
        };

        img.onerror = reject;
      };

      reader.onerror = reject;
    });
  }

  ///function to convert file to base64 string
  toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Failed to convert file to Base64'));
          return;
        }
        const base64 = result.split(',')[1];
        if (!base64) {
          reject(new Error('Base64 content is empty'));
          return;
        }
        resolve(base64);
      };

      reader.onerror = () => {
        reject(new Error('FileReader failed'));
      };

      reader.readAsDataURL(file);
    });
  }
}